/*
 * Mide dónde está el dibujo real (la "tinta") dentro de una imagen con
 * transparencia, ignorando el aire transparente que trae alrededor.
 *
 * Lo usan las pestañas de la sección promocional para darle a cada una el ancho
 * que necesita su etiqueta: los PNG de "SANDWICH" y "DRINKS" vienen en el mismo
 * lienzo, pero el texto de uno es mucho más largo que el del otro, y eso solo se
 * sabe mirando los píxeles.
 *
 * También el botón flotante "Menú", para ubicar el círculo de su diseño y poner
 * la X encima del mismo tamaño.
 *
 * Requiere que el bucket sirva las imágenes con CORS (lo hace: responde
 * `Access-Control-Allow-Origin: *`). Si no se puede leer la imagen, devuelve
 * null y quien la usa cae a un tamaño por defecto.
 *
 * La pide con un parámetro propio (?medir) para que sea OTRA entrada del
 * caché: las <img> visibles la cargan sin CORS, y si compartieran entrada, el
 * navegador (Safari sobre todo) reutiliza la copia sin CORS y la medición
 * falla — o, si la <img> visible pide CORS, es ella la que se rompe. Así las
 * <img> de la página nunca necesitan crossOrigin.
 */

const cache = new Map();

// Ancho al que se reduce la imagen para medirla: de sobra para ubicar los
// bordes y barato de recorrer en un teléfono.
const ANCHO_MUESTRA = 400;

export function medirTinta(url) {
  if (!url) return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);

  const promesa = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const escala = Math.min(1, ANCHO_MUESTRA / img.naturalWidth);
        const w = Math.max(1, Math.round(img.naturalWidth * escala));
        const h = Math.max(1, Math.round(img.naturalHeight * escala));
        const lienzo = document.createElement('canvas');
        lienzo.width = w;
        lienzo.height = h;
        const ctx = lienzo.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);

        let x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            if (data[(y * w + x) * 4 + 3] > 16) {
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
        }
        if (x1 < 0) { resolve(null); return; }

        // Todo en fracciones del lienzo, para que no dependa del tamaño real.
        resolve({
          aspecto: w / h,
          x: x0 / w,
          ancho: (x1 - x0 + 1) / w,
          y: y0 / h,
          alto: (y1 - y0 + 1) / h,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = `${url}${url.includes('?') ? '&' : '?'}medir`;
  });

  cache.set(url, promesa);
  return promesa;
}
